// src/components/filters/DateRangeFilter.tsx
import React, { useState, useEffect } from 'react';

interface DateRangeFilterProps {
  fromDate: Date | null;
  toDate: Date | null;
  onDateRangeChange: (fromDate: Date | null, toDate: Date | null) => void;
  disabled?: boolean;
  label?: string; // Label for the filter (e.g., "Gathering Date" or "Submission Date")
  minDate?: Date | null; // Minimum selectable date for the "from" field
}

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  fromDate,
  toDate,
  onDateRangeChange,
  disabled = false,
  label = 'Date Range',
  minDate = null
}) => {
  // Local state for date inputs before applying
  const [localFromDate, setLocalFromDate] = useState<Date | null>(fromDate);
  const [localToDate, setLocalToDate] = useState<Date | null>(toDate);
  
  // Update local state when props change (e.g., when filters are reset externally)
  useEffect(() => {
    setLocalFromDate(fromDate);
    setLocalToDate(toDate);
  }, [fromDate, toDate]);

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (date: Date | null): string => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  // Parse date from input
  const parseDateFromInput = (dateString: string): Date | null => {
    if (!dateString) return null;
    return new Date(dateString);
  };

  // Handle from date change (local state only)
  const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newFromDate = parseDateFromInput(e.target.value);
    setLocalFromDate(newFromDate);
  };

  // Handle to date change (local state only)
  const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const newToDate = parseDateFromInput(e.target.value);
    setLocalToDate(newToDate);
  };

  // Apply date filters
  const handleApplyFilters = () => {
    if (disabled) return;
    onDateRangeChange(localFromDate, localToDate);
  };

  // Clear date filters
  const handleClearDates = () => {
    if (disabled) return;
    setLocalFromDate(null);
    setLocalToDate(null);
    onDateRangeChange(null, null);
  };

  // Check if local values differ from applied values
  const hasUnappliedChanges = (): boolean => {
    if (disabled) return false;
    
    const fromDatesMatch = (!localFromDate && !fromDate) || 
      (localFromDate && fromDate && localFromDate.getTime() === fromDate.getTime());
    
    const toDatesMatch = (!localToDate && !toDate) || 
      (localToDate && toDate && localToDate.getTime() === toDate.getTime());
    
    return !fromDatesMatch || !toDatesMatch;
  };

  // Check if there are any active filters
  const hasActiveFilters = (): boolean => {
    return !!(fromDate || toDate);
  };

  return (
    <div className={`date-range-filter ${disabled ? 'disabled' : ''}`}>
      {label && <div className="filter-label" style={{ fontWeight: 'bold', marginBottom: '8px' }}>{label}</div>}
      <div className="date-inputs">
        <div className="date-input-group">
          <label htmlFor="from-date">From:</label>
          <input
            id="from-date"
            type="date"
            value={formatDateForInput(localFromDate)}
            onChange={handleFromDateChange}
            className="date-input"
            disabled={disabled}
            min={minDate ? formatDateForInput(minDate) : undefined}
          />
        </div>
        
        <div className="date-input-group">
          <label htmlFor="to-date">To:</label>
          <input
            id="to-date"
            type="date"
            value={formatDateForInput(localToDate)}
            onChange={handleToDateChange}
            className="date-input"
            disabled={disabled}
          />
        </div>
        
        <div className="date-filter-actions">
          <button 
            className="apply-dates-button"
            onClick={handleApplyFilters}
            disabled={!hasUnappliedChanges() || disabled}
          >
            Apply
          </button>
          
          <button 
            className="clear-dates-button"
            onClick={handleClearDates}
            disabled={!hasActiveFilters() || disabled}
          >
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateRangeFilter;